/**
 * Minutes-since-midnight helpers for a booking's "HHmm" times. Mirrors ember
 * utils/bookingValidation (timeToMinutes / endTimeToMinutes / bookingDurationMinutes).
 *
 * Bookings are single-day, so an END at or before the start can only mean the booking runs to
 * midnight ("0000") or past it (a Green-imported "2115-0015"): both read as the end of the day.
 * Reading "0000" as minute 0 hid such bookings from the calendar and from every overlap check.
 */

export const END_OF_DAY_MINUTES = 24 * 60;

/** "HHmm" or "HH:mm" (a leading zero may be missing, e.g. "900") → minutes since midnight. */
export const timeToMinutes = (time: string | number): number => {
  const clean = time.toString().replace(":", "").padStart(4, "0");
  return parseInt(clean.slice(0, 2), 10) * 60 + parseInt(clean.slice(2, 4), 10);
};

/** A booking's END in minutes, with an end at/before the start read as midnight (1440). */
export const endTimeToMinutes = (endTime: string | number, startTime: string | number): number => {
  const end = timeToMinutes(endTime);
  return end <= timeToMinutes(startTime) ? END_OF_DAY_MINUTES : end;
};

/** A booking's length in minutes, midnight-aware. 0 when either time is missing. */
export const bookingDurationMinutes = (
  startTime?: string | number | null,
  endTime?: string | number | null,
): number => {
  if (startTime == null || endTime == null || startTime === "" || endTime === "") return 0;
  return endTimeToMinutes(endTime, startTime) - timeToMinutes(startTime);
};

/** Minutes since midnight → "HHmm" (1440 → "2400", the end-of-day edge). */
export const minutesToHhmm = (mins: number): string =>
  `${Math.floor(mins / 60).toString().padStart(2, "0")}${(mins % 60).toString().padStart(2, "0")}`;
